import React, { Component } from "react";
import {
  Table,
  Row,
  Col,
  FormControl,
  Button,
  Spinner,
  Modal,
  Form,
} from "react-bootstrap";
import CartContext from "../StoreManager/CartContext";
import { fetchCrmList, saveCrmItem, deleteCrmItem } from "./CrmApi";

const emptyActivity = {
  activityId: "",
  storeId: "",
  subject: "",
  description: "",
  activityType: "",
  status: "",
  dueDate: "",
  priority: "",
  contactId: "",
};

export default class CrmActivities extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      activities: [],
      filteredActivities: [],
      loading: true,
      search: "",
      error: null,
      showModal: false,
      isEdit: false,
      currentActivity: { ...emptyActivity },
    };
  }

  componentDidMount() {
    this.loadActivities();
  }

  loadActivities = async () => {
    const storeId = this.context.storeId;
    if (!storeId) {
      this.setState({ loading: false, error: "StoreId is required." });
      return;
    }

    this.setState({ loading: true, error: null });
    try {
      const activities = await fetchCrmList(
        "Activities",
        "GetActivities",
        storeId,
      );
      const normalized = Array.isArray(activities) ? activities : [];
      this.setState({
        activities: normalized,
        filteredActivities: normalized,
        loading: false,
      });
    } catch (error) {
      this.setState({
        loading: false,
        error: error.message || "Unable to load activities.",
      });
    }
  };

  onSearchChange = (event) => {
    const search = event.target.value || "";
    const normalizedSearch = search.trim().toLowerCase();
    const filteredActivities = this.state.activities.filter((activity) => {
      const subject = (activity.subject || activity.title || "").toLowerCase();
      const type = (activity.activityType || activity.type || "").toLowerCase();
      const status = (activity.status || "").toLowerCase();
      return (
        subject.includes(normalizedSearch) ||
        type.includes(normalizedSearch) ||
        status.includes(normalizedSearch)
      );
    });

    this.setState({ search, filteredActivities });
  };

  formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString();
  };

  handleShowModal = (activity = null) => {
    this.setState({
      currentActivity: activity ? { ...activity } : { ...emptyActivity },
      isEdit: Boolean(activity),
      showModal: true,
      error: null,
    });
  };

  handleCloseModal = () => {
    this.setState({ showModal: false, error: null });
  };

  handleFieldChange = (field, value) => {
    this.setState((prevState) => ({
      currentActivity: {
        ...prevState.currentActivity,
        [field]: value,
      },
    }));
  };

  saveActivity = async () => {
    const storeId = this.context.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId is required." });
      return;
    }

    const activity = {
      ...this.state.currentActivity,
      storeId,
      activityId:
        this.state.currentActivity.activityId ||
        "00000000-0000-0000-0000-000000000000",
      dueDate: this.state.currentActivity.dueDate || null,
    };

    try {
      await saveCrmItem("Activities", "AddOrUpdateActivity", activity);
      await this.loadActivities();
      this.handleCloseModal();
    } catch (error) {
      this.setState({ error: error.message || "Unable to save activity." });
    }
  };

  deleteActivity = async (activityId) => {
    const storeId = this.context.storeId;
    if (!storeId) return;
    if (!window.confirm("Delete this activity?")) return;

    try {
      await deleteCrmItem("Activities", "DeleteActivity", storeId, activityId);
      await this.loadActivities();
    } catch (error) {
      this.setState({ error: error.message || "Unable to delete activity." });
    }
  };

  render() {
    const {
      filteredActivities,
      loading,
      search,
      error,
      showModal,
      isEdit,
      currentActivity,
    } = this.state;
    const storeId = this.context.storeId;

    return (
      <div className="m-3">
        <Row className="align-items-center mb-3">
          <Col>
            <h2>CRM Activities</h2>
            <p className="text-muted">
              Manage activities for the selected store.
            </p>
          </Col>
          <Col xs="auto" className="d-flex gap-2">
            <Button variant="primary" onClick={() => this.handleShowModal()}>
              Add Activity
            </Button>
            <Button
              variant="secondary"
              onClick={this.loadActivities}
              disabled={loading}
            >
              {loading ? <Spinner animation="border" size="sm" /> : "Refresh"}
            </Button>
          </Col>
        </Row>

        {!storeId && (
          <div className="alert alert-warning">
            StoreId is required to load CRM activities.
          </div>
        )}
        {error && <div className="alert alert-danger">{error}</div>}

        <Row className="mb-3">
          <Col md={6} lg={4}>
            <FormControl
              placeholder="Search by subject, type, or status"
              value={search}
              onChange={this.onSearchChange}
            />
          </Col>
        </Row>

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Subject</th>
              <th>Type</th>
              <th>Status</th>
              <th>Due Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center py-5">
                  <Spinner animation="border" /> Loading...
                </td>
              </tr>
            ) : filteredActivities.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-5">
                  No activities found.
                </td>
              </tr>
            ) : (
              filteredActivities.map((activity) => (
                <tr key={activity.activityId || Math.random()}>
                  <td>{activity.subject || activity.title || "-"}</td>
                  <td>{activity.activityType || activity.type || "-"}</td>
                  <td>{activity.status || "-"}</td>
                  <td>
                    {this.formatDate(
                      activity.dueDate || activity.dueDateTime || activity.date,
                    )}
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="me-2"
                      onClick={() => this.handleShowModal(activity)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => this.deleteActivity(activity.activityId)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        <Modal show={showModal} onHide={this.handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {isEdit ? "Edit Activity" : "Add Activity"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group controlId="activitySubject">
                    <Form.Label>Subject</Form.Label>
                    <Form.Control
                      value={currentActivity.subject}
                      onChange={(e) =>
                        this.handleFieldChange("subject", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="activityType">
                    <Form.Label>Type</Form.Label>
                    <Form.Control
                      value={currentActivity.activityType}
                      onChange={(e) =>
                        this.handleFieldChange("activityType", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="activityStatus">
                    <Form.Label>Status</Form.Label>
                    <Form.Control
                      value={currentActivity.status}
                      onChange={(e) =>
                        this.handleFieldChange("status", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="activityPriority">
                    <Form.Label>Priority</Form.Label>
                    <Form.Control
                      value={currentActivity.priority}
                      onChange={(e) =>
                        this.handleFieldChange("priority", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="activityDueDate">
                    <Form.Label>Due Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={currentActivity.dueDate?.split("T")[0] || ""}
                      onChange={(e) =>
                        this.handleFieldChange("dueDate", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="activityContactId">
                    <Form.Label>Contact Id</Form.Label>
                    <Form.Control
                      value={currentActivity.contactId}
                      onChange={(e) =>
                        this.handleFieldChange("contactId", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="activityDescription">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={currentActivity.description}
                      onChange={(e) =>
                        this.handleFieldChange("description", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={this.saveActivity}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
